import axios from 'axios';
import {
  clearSession,
  getRefreshToken,
  getToken,
  setAccessToken,
} from './storage';
import API_URL from './apiBase';

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single-flight refresh: while one refresh is in progress, queued 401s wait
// for it instead of firing multiple refresh calls.
let refreshPromise = null;

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    // Use a bare axios call so we don't recurse through this interceptor.
    const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
      refresh_token: refreshToken,
    });
    const newToken = data.access_token || data.token;
    if (newToken) {
      setAccessToken(newToken);
      return newToken;
    }
    return null;
  } catch {
    return null;
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Attempt one transparent refresh + retry on an expired/invalid access token.
    if (status === 401 && original && !original._retry && getRefreshToken()) {
      original._retry = true;
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
    }

    if (status === 401) {
      clearSession();
    }
    return Promise.reject(error);
  }
);

export default api;
